/**
 * Waiver API service (member-facing).
 *
 * Note the auth split: **reading is anonymous, signing is authenticated.**
 * A guest booker has to be able to read the waiver on the payment step,
 * before their account exists.
 *
 * Each function is data-or-throw, so it slots straight into a `useQuery`
 * `queryFn`.
 *
 * @module services/internalApi/waiver/api/waiverService
 */

import { apiClient } from '~/lib/apiClient';
import type { BlobPayload } from '@lgc/database/types';
import type { ActiveWaiver, WaiverStanding, SignWaiverPayload, SignWaiverResult } from '../types';

/**
 * Which of the club's waivers this member still owes. Requires a session.
 *
 * The server applies the resign-scope rule, so `signed: false` means "must sign
 * now" without the client interpreting versions.
 */
export async function checkWaiverStatus(clubId: number): Promise<WaiverStanding[]> {
  const response = await apiClient.get<WaiverStanding[]>(
    `/waivers/users/${clubId}/checkWaiverStatus`,
  );

  if (!response.success) {
    throw new Error(response.message || 'Failed to check waiver status');
  }

  return response.payload;
}

/**
 * The waivers a club currently asks members to sign.
 *
 * Empty array is a valid state — most clubs configure none.
 */
export async function getActiveWaivers(clubId: number): Promise<ActiveWaiver[]> {
  const response = await apiClient.get<ActiveWaiver[]>(
    `/waivers/clubs/${clubId}/getActiveWaivers`,
    { skipAuth: true }, // public endpoint — hit during guest checkout, before a session exists
  );

  if (!response.success) {
    throw new Error(response.message || 'Failed to fetch waivers');
  }

  return response.payload;
}

/**
 * The blank waiver document as a PDF.
 *
 * Anonymous, like `getActiveWaivers` — this backs the "Download PDF" link the
 * member sees while reading, which on the guest path is before any account
 * exists.
 */
export async function downloadWaiverDocument(
  clubId: number,
  waiverVersionId: number,
): Promise<BlobPayload> {
  const response = await apiClient.get<BlobPayload>(
    `/waivers/clubs/${clubId}/downloadWaiverDocument/${waiverVersionId}`,
    { skipAuth: true },
  );

  if (!response.success) {
    throw new Error(response.message || 'Failed to download waiver');
  }

  return response.payload;
}

/**
 * Record a signature. Requires an authenticated session.
 *
 * On the guest path the caller holds the drawn signature until the account
 * exists, then calls this — which is why the signature travels through booking
 * context rather than being posted from the pad itself.
 */
export async function signWaiver(payload: SignWaiverPayload): Promise<SignWaiverResult> {
  const response = await apiClient.post<SignWaiverResult>('/waivers/users/signWaiver', payload);

  if (!response.success) {
    throw new Error(response.message || 'Failed to sign waiver');
  }

  return response.payload;
}
